/* iter233 — Field Service Catalog (mobile-first). Technicians search/browse the
   canonical Service Catalog and see the price for the SELECTED customer tier only.
   Read-only lookup (/api/service-catalog/lookup + /categories). No admin/cost fields. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, ChevronRight, ChevronDown, Tag, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIERS = [
  { value: 'residential', label: 'Residential' },
  { value: 'hsp', label: 'HSP / Warranty' },
  { value: 'property_management', label: 'Property Mgmt' },
  { value: 'commercial', label: 'Commercial' },
];

const priceBadge = (r) => {
  if (!r) return { text: '—', cls: 'text-[#A3A3A3] border-[#3f3f3f] bg-[#262626]' };
  if (r.pricing_method === 'flat') return { text: r.label, cls: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10' };
  if (r.pricing_method === 'estimate_required') return { text: 'Estimate Required', cls: 'text-sky-300 border-sky-500/40 bg-sky-500/10' };
  if (r.pricing_method === 'not_available') return { text: 'Not Available', cls: 'text-[#A3A3A3] border-[#3f3f3f] bg-[#262626]' };
  return { text: 'Needs Review', cls: 'text-orange-300 border-orange-500/40 bg-orange-500/10' };
};

const FieldServiceCatalog = () => {
  const [tier, setTier] = useState('residential');
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [openCat, setOpenCat] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const loadCats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/service-catalog/categories`, { withCredentials: true });
      setCategories(data.categories || []);
    } catch { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ tier });
      if (q.trim()) params.set('q', q.trim());
      if (activeCat) params.set('category', activeCat);
      const { data } = await axios.get(`${API}/service-catalog/lookup?${params.toString()}`, { withCredentials: true });
      setItems(data.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load catalog'); }
    setLoading(false);
  }, [tier, q, activeCat]);

  useEffect(() => { loadCats(); }, [loadCats]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const tierLabel = useMemo(() => TIERS.find((t) => t.value === tier)?.label, [tier]);

  return (
    <div className="p-4 max-w-3xl mx-auto pb-24" data-testid="field-service-catalog">
      <h1 className="text-white font-['Outfit'] text-2xl font-bold mb-1">Service Catalog</h1>
      <p className="text-[#A3A3A3] text-sm mb-3">Showing <b className="text-white">{tierLabel}</b> pricing. Choose the customer type to see the right price.</p>

      {/* Tier selector */}
      <div className="flex flex-wrap gap-2 mb-3" data-testid="field-tier-selector">
        {TIERS.map((t) => (
          <button key={t.value} data-testid={`field-tier-${t.value}`} onClick={() => setTier(t.value)}
            className={`text-xs font-bold px-3 py-1.5 rounded-full border ${tier === t.value ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#3f3f3f] text-[#A3A3A3]'}`}>{t.label}</button>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-3">
        <Search className="w-4 h-4 text-[#A3A3A3] absolute left-3 top-1/2 -translate-y-1/2" />
        <input data-testid="field-catalog-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search SKU, name, category…"
          className="w-full bg-[#0A0A0A] border border-[#262626] rounded-lg pl-9 pr-3 py-2.5 text-white text-sm" />
      </div>

      {/* Category chips */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-2" data-testid="field-category-chips">
        <button onClick={() => setActiveCat(null)} className={`shrink-0 text-xs font-bold px-3 py-1 rounded-full border ${!activeCat ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#3f3f3f] text-[#A3A3A3]'}`}>All ({categories.reduce((a, c) => a + c.count, 0)})</button>
        {categories.map((c) => (
          <button key={c.category} data-testid={`field-cat-${c.category}`} onClick={() => setActiveCat(activeCat === c.category ? null : c.category)}
            className={`shrink-0 text-xs font-bold px-3 py-1 rounded-full border ${activeCat === c.category ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#3f3f3f] text-[#A3A3A3]'}`}>{c.category} ({c.count})</button>
        ))}
      </div>

      {/* Results */}
      {loading ? (
        <p className="text-[#A3A3A3] text-sm py-6 text-center">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-[#A3A3A3] text-sm py-6 text-center" data-testid="field-catalog-empty">No services found.</p>
      ) : (
        <div className="space-y-2" data-testid="field-catalog-results">
          {items.map((it) => {
            const b = priceBadge(it.resolved);
            return (
              <button key={it.id} data-testid={`field-item-${it.sku}`} onClick={() => setDetail(it)}
                className="w-full text-left bg-[#0A0A0A] border border-[#262626] rounded-lg p-3 flex items-center justify-between gap-3 hover:border-[#3f3f3f]">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[#D4AF37] text-xs">{it.sku}</span>
                    <span className="text-[#A3A3A3] text-[11px] truncate">{it.category}</span>
                  </div>
                  <p className="text-white text-sm truncate">{it.name}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`px-2 py-1 rounded text-xs font-bold border ${b.cls}`} data-testid={`field-price-${it.sku}`}>{b.text}</span>
                  <ChevronRight className="w-4 h-4 text-[#525252]" />
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Detail sheet */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="field-item-detail">
          <div className="absolute inset-0 bg-black/70" onClick={() => setDetail(null)} />
          <div className="relative bg-[#141414] border border-[#262626] rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[85vh] overflow-y-auto p-5">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div>
                <span className="font-mono text-[#D4AF37] text-xs">{detail.sku}</span>
                <h3 className="text-white font-bold text-lg">{detail.name}</h3>
                <p className="text-[#A3A3A3] text-xs">{detail.category}{detail.subcategory ? ` · ${detail.subcategory}` : ''}</p>
              </div>
              <button onClick={() => setDetail(null)} className="text-[#A3A3A3]"><X className="w-5 h-5" /></button>
            </div>
            <div className="my-3">
              <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">{tierLabel} price</p>
              <p className={`text-2xl font-bold ${priceBadge(detail.resolved).cls.split(' ')[0]}`} data-testid="field-detail-price">{priceBadge(detail.resolved).text}</p>
            </div>
            {detail.customer_scope && <p className="text-[#C9C9C9] text-sm mb-2">{detail.customer_scope}</p>}
            {(detail.aliases || []).length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {detail.aliases.map((a) => <span key={a} className="inline-flex items-center gap-1 text-[10px] text-[#A3A3A3] border border-[#3f3f3f] rounded px-1.5 py-0.5"><Tag className="w-2.5 h-2.5" />{a}</span>)}
              </div>
            )}
            <p className="text-[11px] text-[#525252] mt-3">To add this to an invoice or estimate, open the job's billing screen and use "Add Service From Catalog".</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldServiceCatalog;
